import type { Location } from "history";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { replace } from "react-router-redux";
import { t } from "ttag";

import { useDispatch, useSelector } from "metabase/redux";

import { login } from "../../actions";
import {
  getExternalAuthProviders,
  getHasSessionCookies,
  getIsLdapEnabled,
} from "../../selectors";
import type { LoginData } from "../../types";
import { AuthButton } from "../AuthButton";
import { LoginForm } from "../LoginForm";

import { ActionList, ActionListItem } from "./PasswordPanel.styled";

interface PasswordPanelProps {
  redirectUrl?: string;
  initialValues?: Pick<LoginData, "username" | "password">;
  location?: Pick<Location, "pathname"> & {
    query?: Record<string, unknown>;
  };
  shouldAutoSubmit?: boolean;
}

export const PasswordPanel = ({
  redirectUrl,
  initialValues,
  location,
  shouldAutoSubmit = false,
}: PasswordPanelProps) => {
  const providers = useSelector(getExternalAuthProviders);
  const isLdapEnabled = useSelector(getIsLdapEnabled);
  const hasSessionCookies = useSelector(getHasSessionCookies);
  const dispatch = useDispatch();
  const cleanupLocation = useMemo(
    () => getCleanLoginLocation(location),
    [location],
  );

  const handleSubmit = useCallback(
    async (data: LoginData) => {
      await dispatch(login({ data, redirectUrl })).unwrap();
      if (cleanupLocation) {
        dispatch(replace(cleanupLocation));
      }
    },
    [cleanupLocation, dispatch, redirectUrl],
  );

  return (
    <div>
      <LoginForm
        isLdapEnabled={isLdapEnabled}
        hasSessionCookies={hasSessionCookies}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <AutoLoginSubmitter shouldAutoSubmit={shouldAutoSubmit} />
      </LoginForm>
      <ActionList>
        <ActionListItem>
          <AuthButton link="/auth/forgot_password">
            {t`I seem to have forgotten my password`}
          </AuthButton>
        </ActionListItem>
        {providers.map((provider) => (
          <ActionListItem key={provider.name}>
            <provider.Button redirectUrl={redirectUrl} />
          </ActionListItem>
        ))}
      </ActionList>
    </div>
  );
};

interface AutoLoginSubmitterProps {
  shouldAutoSubmit: boolean;
}

const AutoLoginSubmitter = ({
  shouldAutoSubmit,
}: AutoLoginSubmitterProps): null => {
  const { submitForm, values } = useFormikContext<LoginData>();
  const attemptedKeysRef = useRef<Set<string>>(new Set());
  const autoLoginKey = `${values.username}\0${values.password}`;

  useEffect(() => {
    if (!shouldAutoSubmit || attemptedKeysRef.current.has(autoLoginKey)) {
      return;
    }

    attemptedKeysRef.current.add(autoLoginKey);
    void submitForm();
  }, [autoLoginKey, shouldAutoSubmit, submitForm]);

  return null;
};

const getCleanLoginLocation = (
  location?: Pick<Location, "pathname"> & {
    query?: Record<string, unknown>;
  },
) => {
  if (!location?.pathname || !location.query) {
    return;
  }

  const searchParams = new URLSearchParams();

  Object.entries(location.query).forEach(([key, value]) => {
    if (typeof value === "string") {
      searchParams.set(key, value);
    }
  });

  if (!searchParams.has("u") && !searchParams.has("p")) {
    return;
  }

  searchParams.delete("u");
  searchParams.delete("p");

  return {
    pathname: location.pathname,
    search: searchParams.toString(),
  };
};
